// game-options-view.ts — the in-game GAME OPTIONS DOM overlay (HTML/DOM UI shell
// plan, Phase 5, Form 4). Renders the kidfInGameOptions 1049 settings form on a
// 160×160 forms.ini.pp design stage OVER the live canvas, modal (eats all input
// while up). It replaces the canvas InGameOptionsForm 1:1.
//
// On WEB, DoModalGameOptionsForm (gameoptions.ts:169) loads kidfInGameOptions
// DIRECTLY (the GameOptions HUB + Sound/Color/Display/Performance sub-forms are the
// DESKTOP arm — NOT reached on web), so this single settings form IS the in-game
// OPTIONS. Lasso Selection is HIDDEN on web (gameoptions.ts:355) and Mouse Input is
// forced ON + disabled — exactly like the shell options form.
//
// The read/apply MIRRORS the canvas InGameOptionsForm EXACTLY, via the pure
// GameOptionsModel (game-options-io.ts) over the SAME ShellEnv seams the canvas
// uses — so this is presentation-only (no sim/command/wire change): OK calls
// env.setGameSpeed() (the RUNNING sim), plrLocal.SetHandicap, getSimUIForm()
// .SetUIType, options.nScrollSpeed/wfHandicap, savePreferences; Mute toggles
// sndm.Enable LIVE; CANCEL reverts the live mute; DEFAULT re-reflects.
//
// Geometry is the SHIPPED forms.ini.pp form 1049 (FORM 0 0 160 160, FORMBACKCOLOR,
// center): title "GAME OPTIONS" (38,6,83) center; "Game Speed" (11,22) + slider
// (11,32,95,8) + value (121,32,17) right; "Scroll Speed" (11,44) + slider
// (11,54,95,8) + value (121,54,17) right; "Difficulty" (11,89) + radios Easy
// (11,99,67) / Normal (11,109) / Hard (11,119); "Miscellaneous" (80,89) +
// checkboxes Mute Sound (80,99,67) / Mouse Input (80,109, disabled); OK (13,140,
// 40,12) / CANCEL (60,140,40,12) / DEFAULT (107,140,40,12).
//
// SFX / animation: like the canvas ShellForm (ShellDoModal fAnimate=true) — every
// control cascades in from an alternating side (formchrome.ts zipInStage), then
// ksfxGuiFormShow. The faithful chrome (backdrop + 9-slice buttons) is the SAME
// pack art the sibling in-game views use.
//
// The design-stage chrome (formScale/makeStage/at/the 9-slice button/formTitle) is the
// SHARED chrome.ts primitives (Phase 2 de-dup); the slider/squareCheck/label are
// view-local widgets. The button uses fontFloor 8.

import type { ButtonArt } from '../title-art.ts';
import { ksfxGuiCheckBoxTap, ksfxGuiButtonTap } from '../../ui/uikit.ts';
import { zipInStage } from '../formchrome.ts';
import type { GameOptionsModel, Difficulty } from './game-options-io.ts';
import {
  COL_BG,
  COL_FG,
  COL_ACCENT,
  TEXT_SHADOW,
  el,
  FONT_BODY,
  buttonWidthU,
  makeStage,
  at,
  formTitle,
  makeStageButton,
  type StageButtonSize,
} from '../chrome.ts';

export interface GameOptionsViewDeps {
  /** the pure read/apply model over the live ShellEnv (game-options-io.ts). */
  model: GameOptionsModel;
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** OK → the host unmounts + resumes (calls done). The view runs model.ok() FIRST. */
  onOk: () => void;
  /** CANCEL → the host unmounts + resumes (calls done). The view runs model.cancel()
   *  FIRST (reverting the live mute). */
  onCancel: () => void;
  /** the title-screen backdrop (titlescreenbkgd.png) as a CSS url; absent ⇒ flat. */
  titleBg?: string;
  /** the raw 9-slice button pieces — faithful chrome; absent ⇒ a CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sfx (ksfxGui*) on a button/check/slider press, wired to SoundMgr. */
  playSfx?: (sfx: number) => void;
}

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

export class GameOptionsView {
  private readonly d: GameOptionsViewDeps;
  /** the shared 9-slice button renderer (owns its own composite cache; fontFloor 8). */
  private readonly btn: (
    label: string,
    on: () => void,
    size?: StageButtonSize,
  ) => HTMLButtonElement;
  /** the ShellForm zip plays ONCE, on the first show — not on re-renders. */
  private animated = false;
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(d: GameOptionsViewDeps) {
    this.d = d;
    this.btn = makeStageButton(d); // fontFloor defaults to 8
    const s = d.root.style;
    // a fixed, full-window MODAL overlay above #screen (z above the canvas at 10);
    // pointer-events:all so the canvas behind it gets nothing while it is up.
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden'; // clip controls parked off-window during the zip (no scrollbar flash)
    // EVERY shell form renders over the title backdrop with a black letterbox
    // (shellform.ts OnPaintBackground) — the options form is FORMBACKCOLOR.
    s.backgroundImage = d.titleBg !== undefined ? `url(${d.titleBg})` : 'none';
    s.backgroundColor = COL_BG;
    s.backgroundSize = 'cover';
    s.backgroundPosition = 'center';
    s.backgroundRepeat = 'no-repeat';
    window.addEventListener('resize', this.onResize);
    this.render();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  /** detach the resize listener + cancel a pending form-show sfx (the host unmounts
   *  the overlay on OK/CANCEL). */
  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    if (this.formShowTimer !== null) {
      clearTimeout(this.formShowTimer);
      this.formShowTimer = null;
    }
  }

  // the ShellForm zip-in — the SAME faithful cascade the sibling in-game views use
  // (formchrome.ts zipInStage ↔ shellform.ts:298-384).
  private zipIn(stage: HTMLElement): void {
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    this.formShowTimer = zipInStage(stage, this.d.playSfx);
  }

  // ── the faithful design stage (forms.ini: the options form is 160×160) ────────
  // The design stage (formScale/makeStage/at) is the shared chrome.ts primitives.
  private stageButton(
    s: number,
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    on: () => void,
  ): HTMLElement {
    return at(this.btn(label, on, { w: w * s, h: h * s }), s, x, y);
  }

  render(): void {
    const { stage, s } = makeStage();
    const m = this.d.model;

    // title "GAME OPTIONS" (38,6,83) center — rendered full-width centered (the
    // canvas centers a too-wide title on its anchor; full-width keeps it on-form).
    stage.append(at(formTitle('GAME OPTIONS', s), s, 0, 6, 160));

    // Game Speed: label (11,22) + slider (11,32,95,8) + value (121,32,17) right.
    stage.append(at(this.label('Game Speed', 'left', s), s, 11, 22, 68));
    const gMax = m.gameSpeedNotchMax();
    stage.append(
      at(
        this.slider(
          gMax > 0 ? m.gameSpeedNotch() / gMax : 0,
          m.gameSpeedLabel(),
          s,
          (f) => {
            m.setGameSpeedNotch(Math.round(f * gMax));
            return { frac: gMax > 0 ? m.gameSpeedNotch() / gMax : 0, label: m.gameSpeedLabel() };
          },
          (lbl) => {
            gsValue.textContent = lbl;
          },
        ),
        s,
        11,
        32,
        95,
        8,
      ),
    );
    // value box widened from the authored 17u → 24u (x 121 → 116) so the wider WI
    // font shows the full readout ("1.0x"/"11.0x") without clipping (font-fit fix I9).
    const gsValue = this.valueLabel(m.gameSpeedLabel(), s);
    stage.append(at(gsValue, s, 116, 32, 24));

    // Scroll Speed: label (11,44) + slider (11,54,95,8) + value (121,54,17) right.
    stage.append(at(this.label('Scroll Speed', 'left', s), s, 11, 44, 68));
    const ssMax = m.scrollSpeedNotchMax();
    stage.append(
      at(
        this.slider(
          ssMax > 0 ? m.scrollSpeedNotch() / ssMax : 0,
          m.scrollSpeedLabel(),
          s,
          (f) => {
            m.setScrollSpeedNotch(Math.round(f * ssMax));
            return {
              frac: ssMax > 0 ? m.scrollSpeedNotch() / ssMax : 0,
              label: m.scrollSpeedLabel(),
            };
          },
          (lbl) => {
            ssValue.textContent = lbl;
          },
        ),
        s,
        11,
        54,
        95,
        8,
      ),
    );
    // value box widened from the authored 17u → 24u (x 121 → 116) — same as Game Speed.
    const ssValue = this.valueLabel(m.scrollSpeedLabel(), s);
    stage.append(at(ssValue, s, 116, 54, 24));

    // Difficulty (11,89) + the 3 radios (mutually exclusive — the square-check look):
    // Easy (11,99,67) / Normal (11,109) / Hard (11,119).
    stage.append(at(this.label('Difficulty', 'left', s), s, 11, 89, 67));
    const diffs: [string, Difficulty, number][] = [
      ['Easy', 'easy', 99],
      ['Normal', 'normal', 109],
      ['Hard', 'hard', 119],
    ];
    for (const [text, d, y] of diffs) {
      stage.append(
        at(
          this.squareCheck(
            text,
            m.difficulty() === d,
            () => {
              m.setDifficulty(d);
              this.render();
            },
            false,
            s,
          ),
          s,
          11,
          y,
          67,
        ),
      );
    }

    // Miscellaneous (80,89) + Mute Sound (80,99,67) + Mouse Input (80,109,67). Lasso
    // Selection (80,119) is HIDDEN on web — omitted.
    stage.append(at(this.label('Miscellaneous', 'left', s), s, 80, 89, 68));
    stage.append(
      at(
        this.squareCheck(
          'Mute Sound',
          m.muted(),
          () => {
            // toggles sndm.Enable LIVE (the canvas), then re-reflect.
            m.setMuted(!m.muted());
            this.render();
          },
          false,
          s,
        ),
        s,
        80,
        99,
        67,
      ),
    );
    // Mouse Input: web no longer forces it (PR #124 — touch devices default to the
    // FingerHandler), so it reflects the real state + is toggleable; OK's SetUIType swaps
    // the live handler (Stylus when on, Finger when off).
    stage.append(
      at(
        this.squareCheck(
          'Mouse Input',
          m.mouseMode(),
          () => {
            m.setMouseMode(!m.mouseMode());
            this.render();
          },
          false,
          s,
        ),
        s,
        80,
        109,
        67,
      ),
    );

    // OK / CANCEL / DEFAULT — each sized to ITS OWN label in the WI button face (+ the
    // cap-aware pad via the shared buttonWidthU), then CENTRED as a row on the 160-wide
    // form. Per-label (not uniform) is REQUIRED here: the form is only 160u, so 3 uniform
    // DEFAULT-width buttons leave no room for the pad once the 9-slice caps are counted —
    // per-label lets the short OK free the space CANCEL/DEFAULT need to clear the frame.
    // OK / CANCEL apply through the model FIRST (the canvas EndForm epilogue), then the host
    // unmounts + resumes; DEFAULT re-reflects (does NOT close) — InitResettableControls.
    const row = [
      {
        label: 'OK',
        on: (): void => {
          m.ok();
          this.d.onOk();
        },
      },
      {
        label: 'CANCEL',
        on: (): void => {
          m.cancel();
          this.d.onCancel();
        },
      },
      {
        label: 'DEFAULT',
        on: (): void => {
          m.setDefaults();
          this.render();
        },
      },
    ].map((b) => ({ ...b, w: buttonWidthU(b.label, 12, s) }));
    const gapU = 6;
    const totalW = row.reduce((a, b) => a + b.w, 0) + gapU * (row.length - 1);
    let bx = Math.max(0, (160 - totalW) / 2);
    for (const b of row) {
      stage.append(this.stageButton(s, b.label, bx, 140, b.w, 12, b.on));
      bx += b.w + gapU;
    }

    this.d.root.replaceChildren(stage);
    // The ShellForm zip + ksfxGuiFormShow play on the FIRST show only (ShellDoModal
    // fAnimate=true). Resize / re-render (after a difficulty/mute/default change)
    // rebuild the stage WITHOUT re-zipping (the canvas zips once, on open).
    if (!this.animated) {
      this.animated = true;
      this.zipIn(stage);
    }
  }

  // ── widgets (the view-local slider/check/label; the title + the 9-slice button are
  //    the chrome.ts primitives — see formTitle/makeStageButton) ───────────────────
  // a single-line label (the canvas LabelControl), left- or right-aligned.
  private label(text: string, align: 'left' | 'right', s: number): HTMLElement {
    const e = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(5.5 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
      textAlign: align,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    e.textContent = text;
    return e;
  }

  // the slider VALUE label (the N.Nx readout at the right of each slider row).
  private valueLabel(text: string, s: number): HTMLElement {
    return this.label(text, 'right', s);
  }

  // the canvas RadioButton/CheckBox look: a square outlined box with an X when set,
  // a label to the right (the difficulty radios AND the misc checkboxes draw this).
  // Single-select vs multi is the caller's onToggle; `disabled` greys + no-ops.
  private squareCheck(
    label: string,
    checked: boolean,
    onToggle: () => void,
    disabled: boolean,
    s: number,
  ): HTMLElement {
    const box = Math.round(7 * s);
    const row = el('label', {
      display: 'flex',
      alignItems: 'center',
      gap: `${Math.round(3 * s)}px`,
      fontFamily: FONT,
      fontSize: `${Math.round(5.5 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? '0.75' : '1',
      whiteSpace: 'nowrap',
    });
    const b = el('span', {
      width: `${box}px`,
      height: `${box}px`,
      border: `${Math.max(1, Math.round(s))}px solid ${COL_ACCENT}`,
      position: 'relative',
      flex: '0 0 auto',
      boxSizing: 'border-box',
    });
    if (checked) {
      const x = COL_ACCENT;
      b.append(
        el('span', {
          position: 'absolute',
          inset: `${Math.max(1, Math.round(s * 0.5))}px`,
          background: `linear-gradient(45deg, transparent 41%, ${x} 41%, ${x} 59%, transparent 59%), linear-gradient(-45deg, transparent 41%, ${x} 41%, ${x} 59%, transparent 59%)`,
        }),
      );
    }
    const t = el('span', {});
    t.textContent = label;
    row.append(b, t);
    if (!disabled) {
      // preventDefault on pointerdown — the mousedown (not the click) is what starts a
      // text selection on a rapid toggle/drag; the click still fires + toggles.
      row.addEventListener('pointerdown', (e) => {
        e.preventDefault();
      });
      row.addEventListener('click', (e) => {
        e.preventDefault();
        this.d.playSfx?.(ksfxGuiCheckBoxTap);
        onToggle();
      });
    }
    return row;
  }

  // a horizontal slider (the canvas SliderControl: a thin track + a small thumb),
  // sized to the design rect. `quantize` applies the dragged fraction to the model
  // and returns the snapped fraction + label; `onLabel` updates the row's value
  // readout during the drag (the model setter is quiet — no re-render mid-drag).
  private slider(
    initialFrac: number,
    _initialLabel: string,
    s: number,
    quantize: (f: number) => { frac: number; label: string },
    onLabel: (label: string) => void,
  ): HTMLElement {
    const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
    const track = el('div', { position: 'relative', height: '100%', cursor: 'pointer' });
    track.append(
      el('div', {
        position: 'absolute',
        left: '0',
        right: '0',
        top: '50%',
        height: `${Math.max(1, Math.round(s))}px`,
        transform: 'translateY(-50%)',
        background: 'rgba(96,156,214,0.95)',
      }),
    );
    const thumb = el('div', {
      position: 'absolute',
      top: '50%',
      width: `${Math.round(3 * s)}px`,
      height: '100%',
      transform: 'translate(-50%, -50%)',
      background: COL_ACCENT,
      left: `${clamp01(initialFrac) * 100}%`,
      boxShadow: '0 0 4px rgba(0,0,0,0.6)',
    });
    track.append(thumb);
    const apply = (clientX: number): void => {
      const r = track.getBoundingClientRect();
      const q = quantize(clamp01((clientX - r.left) / r.width));
      thumb.style.left = `${clamp01(q.frac) * 100}%`;
      onLabel(q.label);
    };
    let dragging = false;
    track.addEventListener('pointerdown', (e) => {
      e.preventDefault(); // don't let the drag start a page text-selection
      dragging = true;
      this.d.playSfx?.(ksfxGuiButtonTap);
      track.setPointerCapture(e.pointerId);
      apply(e.clientX);
    });
    track.addEventListener('pointermove', (e) => {
      if (dragging) apply(e.clientX);
    });
    const end = (e: PointerEvent): void => {
      if (!dragging) return;
      dragging = false;
      try {
        track.releasePointerCapture(e.pointerId);
      } catch {
        /* capture already gone */
      }
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    return track;
  }
}
